// vg-binding: P7.D-08
//
// apps/vendor/src/pages/Products.tsx
//
// Phase 7 Wave 8 Task 27 — My Products list page.

import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { PageHeader, Pagination } from '@printway/ui';

import { useTranslation } from '../lib/i18n/index.js';
import { AnnouncementBanner } from '../features/announcements/AnnouncementBanner.js';
import { ProductsTable } from '../features/products/ProductsTable.js';
import {
  ProductsTabs,
  tabKeyToBeFilter,
  type ProductsTabKey,
} from '../features/products/ProductsTabs.js';
import { useProductsList, type ProductState } from '../features/products/api.js';

// Rows-per-page choices. Capped at 100 to match the BE products list limit
// clamp (routes/products.ts:181 → Math.min(100, …)). Issue #2097 UAT ask.
const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 20;

export function ProductsPage(): JSX.Element {
  const { t } = useTranslation('products');
  const navigate = useNavigate();
  const [tab, setTab] = useState<ProductsTabKey>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [q, setQ] = useState('');
  const beFilter = tabKeyToBeFilter(tab);
  // BR-PAGING: "active-compound" tab unions two BE states client-side, so
  // numbered pagination doesn't apply there — it would lie about total /
  // page_count. Skip the pager for that single tab; show capped 100-row
  // fetch until BE accepts multi-state filter.
  const isCompound = beFilter === 'active-compound';

  const query = useProductsList(
    isCompound
      ? { limit: 500, q: q || undefined }
      : beFilter === null
        ? { limit: pageSize, page, q: q || undefined }
        : { state: beFilter as ProductState, limit: pageSize, page, q: q || undefined },
  );

  // Active-compound: BE returns up to 500 rows unfiltered by state. Client
  // unions {live, inactive_countdown} and applies its own pagination so the
  // user still gets numbered nav consistent with the other tabs.
  const compoundFiltered = useMemo(() => {
    if (!isCompound) return [];
    const all = query.data?.rows ?? [];
    return all.filter(
      (r) => r.state === 'live' || r.state === 'inactive_countdown',
    );
  }, [isCompound, query.data]);

  const rows = useMemo(() => {
    if (isCompound) {
      const start = (page - 1) * pageSize;
      return compoundFiltered.slice(start, start + pageSize);
    }
    return query.data?.rows ?? [];
  }, [isCompound, compoundFiltered, page, pageSize, query.data]);

  const total = isCompound
    ? compoundFiltered.length
    : query.data?.meta.total ?? 0;
  const pageCount = isCompound
    ? Math.max(1, Math.ceil(compoundFiltered.length / pageSize))
    : query.data?.meta.page_count ?? 0;

  const handleTabChange = useCallback((next: ProductsTabKey) => {
    setTab(next);
    setPage(1);
  }, []);

  return (
    <main
      data-testid="products-page-root"
      className="flex flex-col gap-4 px-6 py-6"
    >
      <PageHeader
        title={t('list.pageTitle')}
        actions={
          <Link
            to="/products/new"
            data-testid="products-add-btn"
            className="inline-flex h-9 items-center rounded-md bg-pw-primary px-4 text-sm font-semibold text-white hover:bg-pw-primary-strong"
          >
            {t('list.addButton')}
          </Link>
        }
      />

      {/* Admin system announcement targeted at surface=page:products. */}
      <AnnouncementBanner surface="page:products" />

      <ProductsTabs active={tab} onChange={handleTabChange} />

      <div className="flex items-center gap-2">
        <input
          type="search"
          data-testid="products-search-input"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder={t('list.searchPlaceholder')}
          className="h-9 w-72 rounded-md border border-pw-border bg-pw-bg px-3 text-sm text-pw-text focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
        />
        {q ? (
          <button
            type="button"
            data-testid="products-search-clear"
            onClick={() => {
              setQ('');
              setPage(1);
            }}
            className="text-xs text-pw-text-muted hover:text-pw-text"
          >
            {t('list.searchClear')}
          </button>
        ) : null}
      </div>

      <ProductsTable
        rows={rows}
        isLoading={query.isLoading}
        onSelect={(row) => navigate(`/products/${row._id}`)}
      />

      <Pagination
        currentPage={page}
        pageCount={pageCount}
        totalRows={total}
        testId="products-pagination"
        disabled={query.isFetching}
        onChange={setPage}
        pageSize={pageSize}
        pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </main>
  );
}

export default ProductsPage;
